<script lang="ts">
	import { gsap } from 'gsap';
	import Circle from './Circle.svelte';
	import { browser } from '$app/environment';
	import { untrack } from 'svelte';

	const {
		x,
		y,
		width,
		height
	}: {
		x: number;
		y: number;
		width: number;
		height: number;
	} = $props();

	const smallestSize = $derived(Math.min(width, height));
	const margin = $derived(0.3 * smallestSize * 0.5);
	const r = $derived(smallestSize * 0.5 - margin);

	function getRandomPosition() {
		const allPos = [
			{ x: 0, y: 0 },
			{ x: 0, y: 1 * 1000 },
			{ x: 0, y: -1 * 1000 },
			{ x: 1 * 1000, y: 0 },
			{ x: -1 * 1000, y: 0 }
		];

		return allPos[Math.floor(Math.random() * allPos.length)];
	}

	const center = $derived({ x: x * width + r + margin, y: y * height + r + margin });

	const offset = $state(getRandomPosition());
	const blur = $state({ value: 0 });
	let posTimeout: number;
	let blurTimeout: number;

	function isInCenter() {
		return offset.x === 0 && offset.y === 0;
	}

	function gotoNewPosition() {
		const speed = 2000;
		const delay = isInCenter() ? 12000 : 500;

		clearTimeout(posTimeout);
		posTimeout = window.setTimeout(
			() => {
				gotoNewPosition();
			},
			speed + delay * Math.random()
		);

		const direction = { x: Math.random() > 0.5 ? 1 : -1, y: Math.random() > 0.5 ? 1 : -1 };

		let destination = { ...offset };
		if (isInCenter()) {
			if (Math.random() > 0.5) {
				destination.x += width * direction.x;
			} else {
				destination.y += height * direction.y;
			}
		} else {
			if (Math.random() > 0.5) {
				offset.x = Math.min(Math.max(width * -direction.x, -width), width);
				offset.y = 0;
				destination.x = Math.random() > 0.8 ? width * direction.x : 0;
				destination.y = 0;
			} else {
				offset.x = 0;
				offset.y = Math.min(Math.max(height * -direction.y, -height), height);
				destination.y = Math.random() > 0.8 ? height * direction.y : 0;
				destination.x = 0;
			}
		}

		// Clamp
		destination.x = Math.min(Math.max(destination.x, -width), width);
		destination.y = Math.min(Math.max(destination.y, -height), height);

		// Animate
		gsap.to(offset, {
			x: destination.x,
			y: destination.y,
			duration: speed / 1000,
			ease: 'expo.inOut'
		});
	}

	function gotoNewBlur() {
		gsap.to(blur, {
			value: Math.random() > 0.5 ? 0 : 4,
			duration: 2,
			ease: 'expo.inOut'
		});
		blurTimeout = window.setTimeout(
			() => {
				gotoNewBlur();
			},
			2000 + Math.random() * 1000
		);
	}

	$effect(() => {
		if (!browser) return;
		posTimeout = setTimeout(() => {
			untrack(() => gotoNewPosition());
		}, 4000 * Math.random());
		untrack(() => gotoNewBlur());
		return () => {
			clearTimeout(posTimeout);
			clearTimeout(blurTimeout);
		};
	});
</script>

<Circle x={center.x} y={center.y} {height} {width} {r} blur={blur.value} {offset}></Circle>
